import { useState } from 'react';
import { Menu } from '@mantine/core';
import { IconReceipt, IconUrgent } from '@tabler/icons-react';
import {
  LeadViewExtensions,
  type LeadViewPayload,
} from '@ayocore/erp/crm/client/components/Leads/views/LeadViewExtensions';
import { useLeadRepository } from '@ayocore/erp/crm/client/rpc';
import { createDealFromLead } from '@ayocore/grasias/client/rpc';
import { CRM_LEAD_TYPES } from '@ayocore/erp/crm/constants';
import { notifications } from '@mantine/notifications';
import logger from '@ayocore/platform/common/helpers/logger';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Пункт меню «Создать сделку».
 * Использует leadTypeSlug из payload — без лишнего RPC-запроса.
 */
function CreateDealMenuItem({ lead, leadTypeSlug, reload }: LeadViewPayload) {
  const [creating, setCreating] = useState(false);

  if (leadTypeSlug !== CRM_LEAD_TYPES.INCOMING_REQUEST) return null;

  if (lead.deal_id) {
    return (
      <Menu.Item
        leftSection={<IconReceipt size={14} />}
        component='a'
        href={`/crm/deals/${lead.deal_id}`}
      >
        Сделка #{lead.deal_id}
      </Menu.Item>
    );
  }

  const handleCreate = async () => {
    try {
      setCreating(true);
      const result = await createDealFromLead({ lead_id: lead.id });
      notifications.show({
        title: 'Сделка создана',
        message: `Сделка №${result.deal_number} создана из заявки`,
        color: 'green',
      });
      reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка создания сделки';
      notifications.show({ title: 'Ошибка', message, color: 'red' });
      logger.error(err, '[grasias] createDealFromLead failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Menu.Item
      leftSection={<IconReceipt size={14} />}
      onClick={() => void handleCreate()}
      disabled={creating}
      color='teal'
    >
      {creating ? 'Создание...' : 'Создать сделку'}
    </Menu.Item>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Пункт меню «Пометить как срочную / Снять пометку».
 * Только для INCOMING_REQUEST. Переключает priority между 'urgent' и 'medium'.
 */
function UrgentMenuItem({ lead, leadTypeSlug, reload }: LeadViewPayload) {
  const [loading, setLoading] = useState(false);
  const leadRepo = useLeadRepository();

  if (leadTypeSlug !== CRM_LEAD_TYPES.INCOMING_REQUEST) return null;

  const isUrgent = lead.priority === 'urgent';

  const handleToggle = async () => {
    try {
      setLoading(true);
      const newPriority = isUrgent ? 'medium' : 'urgent';
      await leadRepo.update(lead.id, { priority: newPriority });
      notifications.show({
        title: !isUrgent ? 'Заявка помечена как срочная' : 'Пометка «Срочно» снята',
        message: !isUrgent
          ? 'Заявка отмечена как требующая срочного рассмотрения'
          : 'Пометка срочности удалена',
        color: !isUrgent ? 'red' : 'gray',
      });
      reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка';
      notifications.show({ title: 'Ошибка', message, color: 'red' });
      logger.error(err, '[grasias] urgent toggle failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Menu.Item
      leftSection={<IconUrgent size={14} />}
      onClick={() => void handleToggle()}
      disabled={loading}
      color={isUrgent ? undefined : 'red'}
    >
      {isUrgent ? 'Снять пометку «Срочно»' : 'Пометить как срочную'}
    </Menu.Item>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Регистрирует расширения grasias для LeadView.
 * Вызывается один раз из bootstrap.ts.
 */
export function registerGrasiasLeadViewExtensions(): void {
  LeadViewExtensions.menuActions.register(payload => <CreateDealMenuItem {...payload} />);

  LeadViewExtensions.menuActions.register(payload => <UrgentMenuItem {...payload} />);
}
